import { getSupabaseClient } from "./supabaseClient";
import { OnlineFeaturesStorage } from "./OnlineFeaturesStorage";

export type AuthState = "disabled" | "idle" | "signing-in" | "signed-in" | "error";

/**
 * Owns the one Supabase Auth session the game ever creates: anonymous
 * sign-in, session reuse, and retry after a dropped connection. Nothing
 * else in the app calls `supabase.auth.*` directly.
 *
 * Every entry point funnels through `ensureSignedIn()`, which:
 *  - does nothing at all (no network call) unless Online Features are
 *    enabled - `OnlineFeaturesStorage.isEnabled()` is checked first,
 *    every time;
 *  - reuses an existing session instead of creating a new anonymous user
 *    (checked via `getSession()` before ever calling `signInAnonymously()`);
 *  - de-duplicates concurrent callers (a second call while a sign-in is
 *    already in flight awaits the same attempt, never starts a second
 *    one);
 *  - never throws - a failed sign-in just leaves the game in offline
 *    mode, with `getState() === "error"` for the UI to reflect if it
 *    wants to.
 */
class AuthServiceImpl {
  private state: AuthState = "idle";
  private playerId: string | null = null;
  private inFlight: Promise<void> | null = null;

  getState(): AuthState {
    return this.state;
  }

  getPlayerId(): string | null {
    return this.playerId;
  }

  isSignedIn(): boolean {
    return this.state === "signed-in" && this.playerId !== null;
  }

  /** Fire-and-forget safe: never blocks game load, never throws. */
  async ensureSignedIn(): Promise<boolean> {
    if (!OnlineFeaturesStorage.isEnabled()) {
      this.state = "disabled";
      return false;
    }
    return this.ensureSignedInUnconditionally();
  }

  /**
   * Daily Challenge's entry point: signs in the same way `ensureSignedIn()`
   * does (reusing an existing session, de-duplicating concurrent callers,
   * never throwing), but deliberately does NOT gate on
   * `OnlineFeaturesStorage.isEnabled()` - Daily Challenge is available to
   * every player immediately, never locked behind the campaign's opt-in
   * ONLINE FEATURES prompt (see the PR description). This is also the
   * "reuse an existing anonymous player/device identifier if one already
   * exists" the Daily Challenge spec calls for: a player who already
   * opted into campaign Online Features gets the exact same anonymous
   * `playerId` here, never a second identity.
   */
  async ensureSignedInForDaily(): Promise<boolean> {
    return this.ensureSignedInUnconditionally();
  }

  private async ensureSignedInUnconditionally(): Promise<boolean> {
    if (this.state === "signed-in" && this.playerId) return true;

    if (!this.inFlight) {
      this.inFlight = this.performSignIn().finally(() => {
        this.inFlight = null;
      });
    }
    await this.inFlight;
    return this.state === "signed-in";
  }

  private async performSignIn(): Promise<void> {
    const client = getSupabaseClient();
    if (!client) {
      this.state = "error";
      return;
    }

    this.state = "signing-in";
    try {
      const { data: sessionData } = await client.auth.getSession();
      const existingUserId = sessionData.session?.user?.id;
      if (existingUserId) {
        this.playerId = existingUserId;
        this.state = "signed-in";
        return;
      }

      const { data, error } = await client.auth.signInAnonymously();
      if (error || !data.user) {
        this.state = "error";
        return;
      }
      this.playerId = data.user.id;
      this.state = "signed-in";
    } catch {
      this.state = "error";
    }
  }

  /** Call when the browser regains connectivity. No-op unless a previous attempt actually failed. */
  retryIfNeeded(): void {
    if (this.state === "error") {
      void this.ensureSignedIn();
    }
  }

  /** Test-only: resets in-memory state between tests. */
  __resetForTests(): void {
    this.state = "idle";
    this.playerId = null;
    this.inFlight = null;
  }
}

export const AuthService = new AuthServiceImpl();
